import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { stopsApi, settingsApi } from '../services/api';
import { getPlayer } from '../utils/playerStorage';

const MEDIA_BASE = process.env.REACT_APP_MEDIA_URL || '';
function resolveImageUrl(url) {
  if (!url) return null;
  if (url.startsWith('/media/')) return `${MEDIA_BASE}${url}`;
  return url;
}

function effectivePoints(basePoints, priorCount) {
  if (priorCount === 0) return basePoints;
  if (priorCount === 1) return Math.floor(basePoints * 0.7);
  return Math.floor(basePoints * 0.5);
}

function pointsLabel(basePoints, priorCount) {
  if (priorCount === 0) return `${basePoints} poeng`;
  if (priorCount === 1) return `${Math.floor(basePoints * 0.7)} poeng (70% — 2. lag)`;
  return `${Math.floor(basePoints * 0.5)} poeng (50% — ${priorCount + 1}. lag)`;
}

export default function ChallengePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [stop, setStop] = useState(null);
  const [unlocked, setUnlocked] = useState(false);
  const [challengeData, setChallengeData] = useState(null);
  const [distance, setDistance] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [message, setMessage] = useState('');
  const [answerText, setAnswerText] = useState('');
  const [savingAnswer, setSavingAnswer] = useState(false);
  const [answerSaved, setAnswerSaved] = useState(false);
  const [hidePoints, setHidePoints] = useState(false);
  const [proofPhoto, setProofPhoto] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const fileInputRef = useRef(null);

  const player = getPlayer() || {};

  useEffect(() => {
    Promise.all([
      stopsApi.getById(id),
      settingsApi.get().catch(() => ({ data: {} })),
    ])
      .then(([stopRes, settingsRes]) => {
        setStop(stopRes.data);
        if (stopRes.data.completed_by_teams?.includes(player.teamId)) {
          setCompleted(true);
        }
        if (settingsRes.data.hide_points != null) {
          setHidePoints(settingsRes.data.hide_points);
        }
      })
      .catch(() => navigate('/map', { replace: true }))
      .finally(() => setLoading(false));
  }, [id, navigate, player.teamId]);

  const tryUnlock = useCallback((stopData) => {
    // Bypass stops can be unlocked without GPS — send the stop's own coordinates.
    if (stopData?.bypass_distance_check || stopData?.title?.startsWith('TEST')) {
      stopsApi.unlock(id, stopData.latitude, stopData.longitude)
        .then(res => {
          if (res.data.unlocked) {
            setUnlocked(true);
            setChallengeData(res.data.stop);
          }
        })
        .catch(() => {});
      return;
    }

    if (!navigator.geolocation) {
      setGpsError('GPS er ikke tilgjengelig i denne nettleseren.');
      return;
    }

    setGpsError(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        stopsApi.unlock(id, latitude, longitude)
          .then(res => {
            setDistance(res.data.distance);
            if (res.data.unlocked) {
              setUnlocked(true);
              setChallengeData(res.data.stop);
            } else {
              setMessage(res.data.message);
            }
          })
          .catch(() => {
            setGpsError('Kunne ikke sjekke posisjon. Prøv igjen.');
          });
      },
      (err) => {
        switch (err.code) {
          case err.PERMISSION_DENIED:
            setGpsError('Du må gi tilgang til GPS for å låse opp utfordringen.');
            break;
          case err.POSITION_UNAVAILABLE:
            setGpsError('Posisjonen din er ikke tilgjengelig. Prøv igjen.');
            break;
          default:
            setGpsError('GPS-feil. Prøv igjen.');
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, [id]);

  useEffect(() => {
    if (!loading && stop && !completed) {
      tryUnlock(stop);
    }
  }, [loading, stop, completed, tryUnlock]);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProofPhoto(file);
    const reader = new FileReader();
    reader.onload = (ev) => setProofPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSaveAnswer = async () => {
    if (!answerText.trim()) return;
    setSavingAnswer(true);
    try {
      await stopsApi.submitAnswer(id, player.teamId, answerText.trim());
      setAnswerSaved(true);
    } catch {
      // silently ignore — answer will still be submitted on complete
    } finally {
      setSavingAnswer(false);
    }
  };

  const handleComplete = async () => {
    setCompleting(true);
    setMessage('');
    try {
      let proofPhotoUrl = '';
      if (proofPhoto) {
        setUploadingProof(true);
        const uploadRes = await stopsApi.uploadProof(id, proofPhoto, player.teamId);
        proofPhotoUrl = uploadRes.data.photo_url || '';
        setUploadingProof(false);
      }
      if (challengeData?.question && answerText.trim()) {
        await stopsApi.submitAnswer(id, player.teamId, answerText.trim());
      }
      const res = await stopsApi.complete(id, player.teamId, proofPhotoUrl);
      setCompleted(true);
      const pos = res.data.position;
      const suffix = pos === 1 ? '🥇 Første lag!' : pos === 2 ? '🥈 Andre lag' : `🥉 ${pos}. lag`;
      setMessage(hidePoints
        ? suffix
        : `+${res.data.points_awarded} poeng — ${suffix} (Totalt: ${res.data.total_points})`);
    } catch (err) {
      setUploadingProof(false);
      setMessage(err.response?.data?.error || 'Noe gikk galt.');
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="page loading-page">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="page challenge-page">
      <button className="back-btn" onClick={() => navigate('/map')}>
        ← Tilbake til kart
      </button>

      {stop.image_url && (
        <div className="challenge-image">
          <img src={resolveImageUrl(stop.image_url)} alt={stop.title} />
        </div>
      )}
      <div className="challenge-header">
        <span className="challenge-year">{stop.year}</span>
        <h1>{stop.title}</h1>
        {stop.description && <p className="challenge-desc">{stop.description}</p>}
        {!hidePoints && (
          <div className="challenge-points">
            {pointsLabel(stop.points, stop.completed_by_teams?.filter(tid => tid !== player.teamId).length ?? 0)}
          </div>
        )}
      </div>

      {completed ? (
        <div className="challenge-status completed">
          <div className="status-icon">✓</div>
          <p>Fullført!</p>
          {message && <p className="status-message">{message}</p>}
        </div>
      ) : unlocked ? (
        <div className="challenge-content">
          {challengeData?.challenge_text && (
            <div className="challenge-text-box">
              <h2>Oppgave</h2>
              <p>{challengeData.challenge_text}</p>
            </div>
          )}

          <div className="challenge-question-box">
            <h2>Spørsmål</h2>
            {challengeData?.question && (
              <p className="challenge-question-text">{challengeData.question}</p>
            )}
            <textarea
              className="challenge-answer-input"
              placeholder="Skriv svaret ditt her..."
              value={answerText}
              onChange={e => { setAnswerText(e.target.value); setAnswerSaved(false); }}
              rows={3}
            />
            <button
              className="save-answer-btn"
              onClick={handleSaveAnswer}
              disabled={savingAnswer || !answerText.trim()}
            >
              {savingAnswer ? 'Lagrer...' : answerSaved ? 'Lagret ✓' : 'Lagre svar'}
            </button>
          </div>

          <div className="proof-photo-box">
            <p className="proof-photo-label">📸 Ta et bevis-bilde</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={handlePhotoChange}
            />
            {proofPreview ? (
              <div className="proof-preview-wrap">
                <img src={proofPreview} alt="Bevis" className="proof-preview-img" />
                <button
                  className="proof-retake-btn"
                  onClick={() => { setProofPhoto(null); setProofPreview(null); fileInputRef.current.value = ''; }}
                >
                  Ta nytt bilde
                </button>
              </div>
            ) : (
              <button className="proof-camera-btn" onClick={() => fileInputRef.current.click()}>
                📷 Åpne kamera
              </button>
            )}
          </div>

          {message && !completed && (
            <p className="challenge-error-msg">{message}</p>
          )}

          <button
            className="complete-btn"
            onClick={handleComplete}
            disabled={completing || uploadingProof}
          >
            {uploadingProof ? 'Laster opp bilde...' : completing ? 'Fullfører...' : 'Marker som fullført'}
          </button>
        </div>
      ) : (
        <div className="challenge-status locked">
          <div className="status-icon">📍</div>
          {gpsError ? (
            <p className="error-text">{gpsError}</p>
          ) : (
            <>
              <p>{message || 'Sjekker posisjon...'}</p>
              {distance != null && <p className="distance">{Math.round(distance)}m unna</p>}
            </>
          )}
          <button className="retry-btn" onClick={() => tryUnlock(stop)}>
            Prøv igjen
          </button>
        </div>
      )}
    </div>
  );
}
